import { useState } from 'react';
import { Box, Card, Typography, Button, CircularProgress, Link } from '@mui/material';

import { useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";

import { MuiOtpInput } from 'mui-one-time-password-input';

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from '../../../context/AuthProvider';
import { useAnalytics } from '../../../context/AnalyticsProvider';
import { ANALYTICS_TAGS } from "../../../constants/analyticsTags";


/** 
* OTP confirmation handling card
*/
export default function ConfirmOtpCard() {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const { setTag } = useAnalytics();

  const {
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes,
    formState
  } = useUtils();

  const {
    postConfirmSignIn,
    postSignIn,
    startSession,
    otpUserId,
    setOtpUserId,
    otpSession,
    setOtpSession,
    otpDestination,
    setOtpDestination
  } = useAuth();

  const {
    trigger: confirmSignInTrigger,
    isMutating: confirmSignInIsMutating
  } = useSWRMutation("/auth/confirm-signin", postConfirmSignIn);

  const {
    trigger: signInTrigger,
    isMutating: signInIsMutating
  } = useSWRMutation("/auth/signin", postSignIn);

  const navigate = useNavigate();
  
  const [ otp, setOtp ] = useState("");

  const handleChange = (newValue) => {
    setOtp(newValue);
  };

  const handleOtpComplete = async () => {
    try {
      const otpResponse = await confirmSignInTrigger({
        username: otpUserId,
        session: otpSession,
        otp: otp
      });
      console.info("post otp confirmation response: ", otpResponse);
      setFormError(null);
      setTag(ANALYTICS_TAGS.OTP_SUCCESS);
      startSession(otpResponse.authenticationResult);
      navigate("/");
    } catch (error) {
      setOtp("");
      createAlert("The passcode you entered is incorrect or has expired. Please request a new one and try again.", alertSeverityTypes.error);
      const reason = error?.code || 'unknown';
      setTag(ANALYTICS_TAGS.OTP_FAILED, { reason });
    }
  };

  const resendOtp = async () => {
    try {
      const response = await signInTrigger({
        username: formState.email,
        password: formState.password
      });
      console.info("post sign in for resend otp auth response: ", response);
      if (response && response.challengeName === "SMS_MFA") {
        setOtpUserId(response.challengeParameters.USER_ID_FOR_SRP);
        setOtpSession(response.session);
        setOtpDestination(response.challengeParameters.CODE_DELIVERY_DESTINATION);
        createAlert("OTP successfully resent. Please try again.", alertSeverityTypes.success);
        setTag(ANALYTICS_TAGS.OTP_RESEND_SUCCESS);
      } else {
        const message = "OTP failed to resend. Please try again";
        createAlert(message, alertSeverityTypes.error);
        setTag(ANALYTICS_TAGS.OTP_RESEND_FAILED, { reason: 'invalid_response' });
      }
    } catch (error) {
      const message = "OTP failed to resend. Please try again";
      console.error("post sign in for resend otp auth error", error);
      createAlert(message, alertSeverityTypes.error);
      const reason = error?.code || 'unknown';
      setTag(ANALYTICS_TAGS.OTP_RESEND_FAILED, { reason });
    }
  };

  return clientConfig && (
    <Card
      id="otp-card"
      sx={{
        maxWidth: { md: "80%" },
        mx: "auto",
        mb: "350px",
        px: { xs: 2, sm: 3 },
        py: 3,
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          component='h1'
          variant='h5'
          sx={{ mb: 1, fontWeight: "bold" }}
        >
          Hello,
        </Typography>
        <Typography component='h2' variant='p'>
          We've sent you a one-time passcode (OTP) by {otpDestination} to your mobile number. Please enter the 6 digit code below.
        </Typography>
      </Box>

      <Box sx={{ mb: 1 }}>
        <MuiOtpInput
          value={otp}
          onChange={handleChange}
          length={6}
          autoFocus
          validateChar={(char) => !isNaN(char)}
          sx={{
            '& .MuiInputBase-input': {
              width: {xs: '10px', sm: '20px', md: '25px'},
              height: {xs: '10px', sm: '20px', md: '25px'},
              fontSize: {xs: '14px', md: '30px'},
              borderRadius: "50px"
            }
          }}
        />
      </Box>

      <Link
        component="button"
        variant="body2"
        onClick={resendOtp}
      >
        Resend OTP
      </Link>

      {/* Button section */}
      <Box sx={{ mt: 4, mb: 5 }}>
        <Button
          type='button'
          onClick={handleOtpComplete}
          variant='contained'
          sx={{
            borderRadius: clientConfig.layout.buttonBorderRadius,
            width: { xs: "70%", sm: "25%" },
            backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
            color: clientConfig.layout.primaryButtonColour,
            "&:hover": {
              backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
              color: clientConfig.layout.hoverPrimaryButtonColour,
            }
          }}
        >
          Confirm OTP
        </Button>
      </Box>
      {/* End of button section */}
    
      {/* Error message and loading section */}
      <Box sx={{ position: "relative", mt: 4 }}>
        {(confirmSignInIsMutating || signInIsMutating) && (
          <CircularProgress
            size={24}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
          />
        )}
        <Typography
          sx={{ ml: 1, display: "inline-block", mb: { xs: 2 } }}
          color='error'
        >
          {formError && `* ${formError}`}
        </Typography>
      </Box>
      {/* End of error message and loading section */}

    </Card>
  )
};